(() => {
  if (window.AWAnalytics?.version) return;

  const PREFIX = 'assassin_workshop:';
  const memory = new Set();
  const pending = { compatibility: null };
  const debug = new URLSearchParams(window.location.search).get('aw_analytics_debug') === '1';
  const themeRole = String(window.Shopify?.theme?.role || '').toLowerCase();
  const isPreview = Boolean(
    window.Shopify?.designMode ||
    (themeRole && themeRole !== 'main') ||
    /(?:\?|&)preview_theme_id=/.test(window.location.search) ||
    /(?:\?|&)previewPath=/.test(window.location.search)
  );

  const normalize = (value = '') => String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

  const safePrimitive = (value) => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === 'boolean' || typeof value === 'number') return value;
    if (typeof value === 'string') return value.slice(0, 180);
    if (Array.isArray(value)) return value.slice(0, 12).map(safePrimitive).filter((item) => item !== undefined);
    return undefined;
  };

  const sanitize = (payload = {}) => Object.entries(payload).reduce((acc, [key, value]) => {
    const safeKey = normalize(key).slice(0, 60);
    const safeValue = safePrimitive(value);
    if (safeKey && safeValue !== undefined) acc[safeKey] = safeValue;
    return acc;
  }, {});

  const common = () => ({
    schema_version: 1,
    path: window.location.pathname.slice(0, 180),
    locale: document.documentElement.lang || '',
    currency: window.Shopify?.currency?.active || '',
    preview_mode: isPreview
  });

  const track = (name, payload = {}) => {
    const event = normalize(name);
    if (!event) return;
    const data = { ...common(), ...sanitize(payload) };
    const eventName = `${PREFIX}${event}`;

    window.dispatchEvent(new CustomEvent('aw:analytics', {
      detail: { event: eventName, payload: data, suppressed: isPreview }
    }));

    if (debug) console.info('[AW Analytics]', eventName, data, isPreview ? '(preview — non publié)' : '');
    if (isPreview) return;

    const publishShopify = (attempt = 0) => {
      try {
        if (window.Shopify?.analytics?.publish) {
          window.Shopify.analytics.publish(eventName, data);
          return;
        }
      } catch (error) {
        if (debug) console.warn('[AW Analytics] Shopify publish failed', error);
        return;
      }
      if (attempt < 12) window.setTimeout(() => publishShopify(attempt + 1), 250);
    };
    publishShopify();

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `aw_${event}`, ...data });
    } catch (_) {}
  };

  const trackOnce = (key, name, payload = {}) => {
    const token = `${key}`;
    if (memory.has(token)) return;
    memory.add(token);
    track(name, payload);
  };

  const productHandleFromPath = () => {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? match[1] : '';
  };

  const resultState = (root) => {
    const result = root.querySelector('[data-aw-result]');
    if (!result) return 'unknown';
    const match = [...result.classList].find((item) => item.startsWith('aw-compatibility__result--'));
    return match ? match.replace('aw-compatibility__result--', '').replace(/-/g, '_') : 'unknown';
  };

  const compatibilitySnapshot = (root, productData = {}) => {
    const platform = root.querySelector('[data-aw-platform-select]');
    const manufacturer = root.querySelector('[data-aw-manufacturer-select]');
    const replicaType = root.querySelector('[data-aw-replica-type-select]');
    const scoreText = root.querySelector('[data-aw-score-value]')?.textContent || '';
    return {
      product_handle: productHandleFromPath(),
      selected_platform: platform?.value || '',
      selected_manufacturer: manufacturer?.value || '',
      selected_replica_type: replicaType?.value || '',
      result: resultState(root),
      score: Number.parseInt(scoreText, 10) || 0,
      platform_data_count: Array.isArray(productData.platforms) ? productData.platforms.length : 0,
      manufacturer_data_count: Array.isArray(productData.manufacturers) ? productData.manufacturers.length : 0,
      replica_type_data_count: Array.isArray(productData.replicaTypes) ? productData.replicaTypes.length : 0,
      workshop_recommended: productData.workshopInstallation === true,
      installation_level: productData.installationLevel || ''
    };
  };

  const initCompatibility = (root) => {
    if (!root || root.dataset.awAnalyticsReady === 'true') return;
    const dataNode = root.querySelector('[data-aw-product-compatibility]');
    let productData = {};
    try { productData = JSON.parse(dataNode?.textContent || '{}'); } catch (_) {}

    const base = compatibilitySnapshot(root, productData);
    trackOnce(`compatibility_viewed:${base.product_handle}`, 'compatibility_viewed', {
      product_handle: base.product_handle,
      has_platform_data: base.platform_data_count > 0,
      platform_data_count: base.platform_data_count,
      manufacturer_data_count: base.manufacturer_data_count,
      replica_type_data_count: base.replica_type_data_count,
      workshop_recommended: base.workshop_recommended,
      installation_level: base.installation_level
    });

    let interacted = false;
    let lastCheck = null;
    const capture = (source) => window.setTimeout(() => {
      const snapshot = compatibilitySnapshot(root, productData);
      if (!snapshot.selected_platform) return;
      interacted = true;
      lastCheck = snapshot;
      track('compatibility_checked', { ...snapshot, interaction_source: source });
      if (snapshot.workshop_recommended) {
        trackOnce(`workshop_recommendation:${snapshot.product_handle}`, 'workshop_recommendation_exposed', {
          product_handle: snapshot.product_handle,
          compatibility_result: snapshot.result,
          compatibility_score: snapshot.score,
          installation_level: snapshot.installation_level
        });
      }
    }, 0);

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-aw-platform-button]')) capture('platform_button');
    });
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-aw-platform-select]')) capture('platform_select');
      if (event.target.matches('[data-aw-manufacturer-select]')) capture('manufacturer_select');
      if (event.target.matches('[data-aw-replica-type-select]')) capture('replica_type_select');
    });

    document.addEventListener('submit', (event) => {
      if (!interacted || !lastCheck) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !String(form.action || '').includes('/cart/add')) return;
      const variantId = form.querySelector('[name="id"]')?.value || '';
      pending.compatibility = { ...lastCheck, variant_id: variantId, time: Date.now() };
      track('compatibility_add_to_cart_intent', { ...lastCheck, variant_id: variantId });
    }, true);

    root.dataset.awAnalyticsReady = 'true';
  };

  document.addEventListener('cart:updated', () => {
    const now = Date.now();
    if (pending.compatibility && now - pending.compatibility.time < 15000) {
      track('compatibility_add_to_cart_confirmed', pending.compatibility);
      pending.compatibility = null;
    }
  });

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('[data-aw-compatibility]').forEach(initCompatibility);
  };

  window.AWAnalytics = {
    version: '1.1.0',
    track,
    trackOnce,
    preview: isPreview,
    events: {
      compatibilityUsage: ['compatibility_viewed', 'compatibility_checked'],
      compatibilityToCart: ['compatibility_checked', 'compatibility_add_to_cart_confirmed'],
      workshopOpportunity: ['workshop_recommendation_exposed']
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

/* Attribution V2 */
(() => {
  if (window.AWAttribution?.version) return;

  const SCHEMA = '2';
  const ATTRIBUTION_KEY = 'aw_cart_attribution_v2';
  const CART_STATE_KEY = 'aw_cart_measurement_v2';
  const pending = { compatibility: null, configurator: null };
  const config = window.AWAnalyticsConfig || {};
  const workshopHandles = new Set((config.workshopHandles || []).map(String));
  const nativeFetch = window.fetch?.bind(window);

  const readJson = (storage, key, fallback = {}) => {
    try {
      const value = JSON.parse(storage.getItem(key) || 'null');
      return value ?? fallback;
    } catch (_) {
      return fallback;
    }
  };

  const writeJson = (storage, key, value) => {
    try { storage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };

  const track = (name, payload = {}) => {
    try { window.AWAnalytics?.track?.(name, payload); } catch (_) {}
  };

  const resultState = (root) => {
    const result = root?.querySelector('[data-aw-result]');
    const match = result ? [...result.classList].find((item) => item.startsWith('aw-compatibility__result--')) : '';
    return match ? match.replace('aw-compatibility__result--', '').replace(/-/g, '_') : '';
  };

  const compatibilitySnapshot = (root) => {
    if (!root) return null;
    const dataNode = root.querySelector('[data-aw-product-compatibility]');
    let productData = {};
    try { productData = JSON.parse(dataNode?.textContent || '{}'); } catch (_) {}
    const score = Number.parseInt(root.querySelector('[data-aw-score-value]')?.textContent || '', 10) || 0;
    const platform = root.querySelector('[data-aw-platform-select]')?.value || '';
    if (!platform) return null;
    return {
      source: 'compatibility',
      platform,
      manufacturer: root.querySelector('[data-aw-manufacturer-select]')?.value || '',
      replica_type: root.querySelector('[data-aw-replica-type-select]')?.value || '',
      result: resultState(root),
      score,
      workshop_recommended: productData.workshopInstallation === true,
      installation_level: productData.installationLevel || '',
      time: Date.now()
    };
  };

  const configuratorSnapshot = (root) => {
    if (!root) return null;
    const state = readJson(localStorage, root.dataset.storageKey || 'aw_airsoft_configurator', {});
    if (!state.platform) return null;
    return {
      source: 'configurator',
      configuration_name: state.name || '',
      platform: state.platform || '',
      manufacturer: state.manufacturer || '',
      replica_type: state.type || '',
      goal: state.goal || '',
      selected_count: Array.isArray(state.selected) ? state.selected.length : 0,
      time: Date.now()
    };
  };

  const isFresh = (entry) => Boolean(entry && Date.now() - Number(entry.time || 0) < 20000);

  const currentAttribution = () => {
    if (isFresh(pending.configurator)) return pending.configurator;
    if (isFresh(pending.compatibility)) return pending.compatibility;
    return null;
  };

  const propertiesFor = (entry = {}) => {
    const values = {
      '_AW Schema': SCHEMA,
      '_AW Source': entry.source || '',
      '_AW Preview': window.AWAnalytics?.preview ? 'true' : 'false',
      '_AW Platform': entry.platform || '',
      '_AW Manufacturer': entry.manufacturer || '',
      '_AW Replica Type': entry.replica_type || '',
      '_AW Configuration': entry.configuration_name || '',
      '_AW Goal': entry.goal || '',
      '_AW Compatibility Result': entry.result || '',
      '_AW Compatibility Score': entry.score === undefined ? '' : String(entry.score),
      '_AW Workshop Recommended': entry.workshop_recommended === true ? 'true' : (entry.workshop_recommended === false ? 'false' : ''),
      '_AW Installation Level': entry.installation_level || ''
    };
    return Object.fromEntries(
      Object.entries(values)
        .filter(([, value]) => value !== '')
        .map(([key, value]) => [key, String(value).slice(0, 240)])
    );
  };

  const decorateBody = (body, entry) => {
    const properties = propertiesFor(entry);
    if (!Object.keys(properties).length) return body;

    if (body instanceof FormData) {
      Object.entries(properties).forEach(([key, value]) => body.set(`properties[${key}]`, value));
      return body;
    }

    if (body instanceof URLSearchParams) {
      Object.entries(properties).forEach(([key, value]) => body.set(`properties[${key}]`, value));
      return body;
    }

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (Array.isArray(parsed?.items)) {
          parsed.items = parsed.items.map((item) => ({
            ...item,
            properties: { ...(item.properties || {}), ...properties }
          }));
        } else if (parsed && typeof parsed === 'object') {
          parsed.properties = { ...(parsed.properties || {}), ...properties };
        }
        return JSON.stringify(parsed);
      } catch (_) {}
    }

    return body;
  };

  const remember = (entry) => {
    if (!entry) return;
    writeJson(sessionStorage, ATTRIBUTION_KEY, {
      source: entry.source || '',
      platform: entry.platform || '',
      workshop_recommended: entry.workshop_recommended === true,
      time: Date.now()
    });
  };

  const itemProperties = (item) => item?.properties && typeof item.properties === 'object' ? item.properties : {};

  const summarizeCart = (cart) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const attributed = items.filter((item) => {
      const props = itemProperties(item);
      return Boolean(props['_AW Source'] || props['Plateforme AW'] || props['Configuration AW']);
    });
    const sources = [...new Set(attributed.map((item) => itemProperties(item)['_AW Source']).filter(Boolean))];
    return {
      token: cart?.token || '',
      item_count: Number(cart?.item_count || 0),
      aw_attributed: attributed.length > 0,
      attributed_item_count: attributed.length,
      source: sources.join(','),
      workshop_recommended: attributed.some((item) => itemProperties(item)['_AW Workshop Recommended'] === 'true'),
      workshop_service_attached: items.some((item) => workshopHandles.has(String(item.handle || '')))
    };
  };

  let inspection = null;
  const inspectCart = (reason = 'update') => {
    if (!nativeFetch || inspection) return inspection || Promise.resolve(null);
    const root = window.Shopify?.routes?.root || '/';
    inspection = nativeFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then((cart) => {
        if (!cart) return null;
        const state = summarizeCart(cart);
        const previous = readJson(sessionStorage, CART_STATE_KEY, {});
        writeJson(sessionStorage, CART_STATE_KEY, state);

        const changed = previous.token !== state.token ||
          previous.item_count !== state.item_count ||
          previous.attributed_item_count !== state.attributed_item_count ||
          previous.workshop_recommended !== state.workshop_recommended ||
          previous.workshop_service_attached !== state.workshop_service_attached;

        if (changed && state.aw_attributed) track('aw_cart_state', { ...state, inspection_reason: reason });
        if (state.workshop_recommended && state.workshop_service_attached && !previous.workshop_service_attached) {
          track('workshop_service_attached', state);
        }
        return state;
      })
      .catch(() => null)
      .finally(() => { inspection = null; });
    return inspection;
  };

  if (nativeFetch) {
    window.fetch = (input, init = {}) => {
      let attribution = null;
      let nextInit = init;
      let cartMutation = false;
      try {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const method = String(init?.method || input?.method || 'GET').toUpperCase();
        const cartAdd = method === 'POST' && /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(url);
        cartMutation = method === 'POST' && /\/cart\/(?:add|update|change|clear)(?:\.js)?(?:[?#]|$)/i.test(url);
        if (cartAdd) {
          attribution = currentAttribution();
          if (attribution && Object.prototype.hasOwnProperty.call(init, 'body')) {
            nextInit = { ...init, body: decorateBody(init.body, attribution) };
          }
        }
      } catch (_) {}

      const request = nativeFetch(input, nextInit);
      const storedAttribution = readJson(sessionStorage, ATTRIBUTION_KEY, {});
      const storedCartState = readJson(sessionStorage, CART_STATE_KEY, {});
      const shouldInspect = cartMutation && Boolean(attribution || storedAttribution.source || storedCartState.aw_attributed);
      if (!attribution && !shouldInspect) return request;

      return request.then((response) => {
        if (response?.ok && attribution) {
          remember(attribution);
          if (attribution.source === 'compatibility') pending.compatibility = null;
          if (attribution.source === 'configurator') pending.configurator = null;
        }
        if (response?.ok && shouldInspect) {
          window.setTimeout(() => inspectCart(attribution ? 'attributed_add' : 'cart_mutation'), 80);
        }
        return response;
      });
    };
  }

  window.addEventListener('aw:compatibility-changed', (event) => {
    const detail = event.detail || {};
    const modelId = safe(detail.modelId || '', 100);
    if (!modelId) return;
    const entry = {
      source: 'compatibility',
      surface: 'compatibility_search',
      token: token(),
      platform: safe(detail.platform || '', 100),
      manufacturer: safe(detail.manufacturerLabel || detail.manufacturer || '', 100),
      replica_type: safe(detail.replicaTypeLabel || detail.replicaType || '', 100),
      result: 'model_selected',
      score: 100,
      exact_models: [modelId],
      model_state: 'exact_one',
      captured_at: new Date().toISOString(),
      time: Date.now()
    };
    write(sessionStorage, SEARCH_MODEL_KEY, entry);
    track('moat_compatibility_model_selected', {
      surface: entry.surface,
      model_state: entry.model_state,
      exact_model_count: 1
    });
  });

  document.addEventListener('click', (event) => {
    const card = event.target?.closest?.('[data-aw-moat-compat-product]');
    const link = event.target?.closest?.('a[href*="/products/"]');
    if (!card || !link || !card.contains(link)) return;
    const selected = read(sessionStorage, SEARCH_MODEL_KEY, null);
    if (!selected || Date.now() - Number(selected.time || 0) > MAX_AGE || selected.model_state !== 'exact_one') return;
    const href = safe(link.getAttribute('href') || '', 240);
    const match = href.match(/\/products\/([^/?#]+)/i);
    if (!match) return;
    const entry = {
      ...selected,
      surface: 'compatibility_search',
      target_product_handle: decodeURIComponent(match[1]),
      target_product_id: safe(card.dataset.productId || '', 100),
      target_variant_id: safe(card.dataset.variantId || '', 100),
      score: Math.max(0, Math.min(100, Number(card.dataset.awCompatScore || selected.score || 0))),
      result: card.dataset.awCompatRecommended === 'true' ? 'verified_recommended' : 'catalog_match',
      captured_at: new Date().toISOString(),
      time: Date.now()
    };
    write(sessionStorage, COMPAT_KEY, entry);
    track('moat_compatibility_product_clicked', {
      surface: entry.surface,
      model_state: entry.model_state,
      exact_model_count: 1,
      score: entry.score,
      recommendation_verified: card.dataset.awCompatRecommended === 'true'
    });
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    if (String(form.action || '').includes('/cart/add')) {
      const compatibility = document.querySelector('[data-aw-compatibility]');
      const snapshot = compatibilitySnapshot(compatibility);
      if (snapshot) pending.compatibility = snapshot;
    }

    if (form.querySelector('[name="checkout"]') || form.classList.contains('cart-drawer__checkout-form')) {
      const state = readJson(sessionStorage, CART_STATE_KEY, {});
      const attribution = readJson(sessionStorage, ATTRIBUTION_KEY, {});
      track('checkout_intent', {
        aw_attributed: Boolean(state.aw_attributed),
        attributed_item_count: Number(state.attributed_item_count || 0),
        source: state.source || attribution.source || '',
        workshop_recommended: Boolean(state.workshop_recommended),
        workshop_service_attached: Boolean(state.workshop_service_attached)
      });
    }
  }, true);

  document.addEventListener('click', (event) => {
    const addAll = event.target.closest('[data-aw-config-add-all]');
    if (addAll) {
      const root = addAll.closest('[data-aw-configurator]');
      const snapshot = configuratorSnapshot(root);
      if (snapshot) pending.configurator = snapshot;
    }

    if (event.target.closest('a[href*="/checkout"]')) {
      const state = readJson(sessionStorage, CART_STATE_KEY, {});
      const attribution = readJson(sessionStorage, ATTRIBUTION_KEY, {});
      track('checkout_intent', {
        aw_attributed: Boolean(state.aw_attributed),
        attributed_item_count: Number(state.attributed_item_count || 0),
        source: state.source || attribution.source || '',
        workshop_recommended: Boolean(state.workshop_recommended),
        workshop_service_attached: Boolean(state.workshop_service_attached)
      });
    }
  }, true);

  document.addEventListener('cart:updated', () => inspectCart('cart_updated'));
  document.addEventListener('cart:page-updated', () => inspectCart('cart_page_updated'));

  window.AWAttribution = {
    version: '2.0.0',
    schema: SCHEMA,
    inspectCart,
    propertiesFor
  };
})();


/* P2.10 MOAT ATTRIBUTION BRIDGE v1.0.0 */
(() => {
  if (window.AWMoatAttribution?.version) return;

  const VERSION = '1.2.1';
  const SCHEMA = '1';
  const COMPAT_KEY = 'aw_moat_compat_attribution_v1';
  const TOKEN_KEY = 'aw_moat_session_token_v1';
  const ATELIER_KEY = 'aw_moat_atelier_visit_v1';
  const MOAT_EVENTS_KEY = 'aw_moat_events_v1';
  const SEARCH_MODEL_KEY = 'aw_moat_compat_search_model_v1';
  const FUNNEL_ASSIST_KEY = 'aw_p2_assist_v1';
  const MAX_AGE = 30 * 60 * 1000;
  const ATELIER_RETURN_GAP = 24 * 60 * 60 * 1000;
  const previousFetch = window.fetch?.bind(window);

  const safe = (value = '', max = 220) => String(value ?? '').trim().slice(0, max);
  const normalize = (value = '') => safe(value, 160)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const currentProductHandle = () => {
    const match = location.pathname.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const read = (storage, key, fallback = null) => {
    try {
      const parsed = JSON.parse(storage.getItem(key) || 'null');
      return parsed ?? fallback;
    } catch (_) {
      return fallback;
    }
  };
  const write = (storage, key, value) => {
    try { storage.setItem(key, JSON.stringify(value)); return true; } catch (_) { return false; }
  };

  const token = () => {
    let value = safe(sessionStorage.getItem(TOKEN_KEY) || '', 80);
    if (value) return value;
    try {
      value = crypto.randomUUID();
    } catch (_) {
      value = 'aw-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
    }
    try { sessionStorage.setItem(TOKEN_KEY, value); } catch (_) {}
    return value;
  };

  const track = (name, payload = {}) => {
    try { window.AWAnalytics?.track?.(name, payload); } catch (_) {}
  };

  const resultState = (root) => {
    const result = root?.querySelector?.('[data-aw-result]');
    const cls = result ? [...result.classList].find((item) => item.startsWith('aw-compatibility__result--')) : '';
    return cls ? cls.replace('aw-compatibility__result--', '').replace(/-/g, '_') : '';
  };

  const compatibilitySnapshot = (root) => {
    if (!root) return null;
    const dataNode = root.querySelector('[data-aw-product-compatibility]');
    let data = {};
    try { data = JSON.parse(dataNode?.textContent || '{}'); } catch (_) {}

    const platform = safe(root.querySelector('[data-aw-platform-select]')?.value || '', 100);
    if (!platform) return null;

    const manufacturer = safe(root.querySelector('[data-aw-manufacturer-select]')?.value || '', 100);
    const replicaType = safe(root.querySelector('[data-aw-replica-type-select]')?.value || '', 100);
    const manufacturerNorm = normalize(manufacturer);
    const replicaTypeNorm = normalize(replicaType);

    let exactModels = Array.isArray(data.exactModels) ? data.exactModels : [];
    if (manufacturerNorm) {
      const filtered = exactModels.filter((m) => normalize(m?.manufacturer) === manufacturerNorm);
      if (filtered.length) exactModels = filtered;
    }
    if (replicaTypeNorm) {
      const filtered = exactModels.filter((m) => normalize(m?.replicaType) === replicaTypeNorm);
      if (filtered.length) exactModels = filtered;
    }
    let handles = [...new Set(exactModels.map((m) => safe(m?.handle, 100)).filter(Boolean))].slice(0, 12);
    let modelState = handles.length === 1 ? 'exact_one' : (handles.length > 1 ? 'ambiguous_multi' : 'none');

    const prior = read(sessionStorage, COMPAT_KEY, null);
    const priorFresh = Boolean(prior && Date.now() - Number(prior.time || 0) <= MAX_AGE);
    const sameTarget = priorFresh
      && prior.target_product_handle
      && normalize(prior.target_product_handle) === normalize(data.productHandle || currentProductHandle());
    if (sameTarget && prior.model_state === 'exact_one' && Array.isArray(prior.exact_models) && prior.exact_models.length === 1) {
      handles = prior.exact_models.slice(0, 1);
      modelState = 'exact_one';
    }

    return {
      source: 'compatibility',
      surface: sameTarget ? 'compatibility_search_to_pdp' : 'pdp_compatibility',
      token: sameTarget && prior.token ? prior.token : token(),
      product_id: safe(data.productId, 100),
      product_handle: safe(data.productHandle, 120),
      platform,
      manufacturer,
      replica_type: replicaType,
      result: safe(resultState(root), 60),
      score: Number.parseInt(root.querySelector('[data-aw-score-value]')?.textContent || '', 10) || 0,
      exact_models: handles,
      model_state: modelState,
      captured_at: new Date().toISOString(),
      time: Date.now()
    };
  };

  const rememberCompatibility = (root) => {
    const snapshot = compatibilitySnapshot(root);
    if (!snapshot) return null;
    write(sessionStorage, COMPAT_KEY, snapshot);
    return snapshot;
  };

  const currentCompatibility = () => {
    const entry = read(sessionStorage, COMPAT_KEY, null);
    if (!entry || Date.now() - Number(entry.time || 0) > MAX_AGE) return null;
    const currentHandle = currentProductHandle();
    if (entry.target_product_handle && currentHandle
      && normalize(entry.target_product_handle) !== normalize(currentHandle)) return null;
    return entry;
  };

  const currentFunnelAssist = () => {
    const assist = read(sessionStorage, FUNNEL_ASSIST_KEY, null);
    if (!assist || !assist.clicked_at || Date.now() - Number(assist.clicked_at) > MAX_AGE) return null;
    if (!['atelier', 'recommendation', 'garage'].includes(String(assist.source || ''))) return null;
    const currentHandle = currentProductHandle();
    if (assist.target_product_handle && currentHandle
      && normalize(assist.target_product_handle) !== normalize(currentHandle)) return null;
    return {
      source: String(assist.source || ''),
      surface: safe(assist.source_detail || 'assisted_click', 80),
      token: safe(assist.assist_id || token(), 80),
      platform: '',
      result: 'assisted_click',
      score: 0,
      attribution_score: assist.target_product_handle ? 95 : 80,
      exact_models: [],
      model_state: 'none',
      target_product_handle: safe(assist.target_product_handle || '', 120),
      captured_at: new Date(Number(assist.clicked_at) || Date.now()).toISOString(),
      time: Number(assist.clicked_at) || Date.now()
    };
  };

  const currentAttribution = () => {
    const entries = [currentCompatibility(), currentFunnelAssist()].filter(Boolean);
    entries.sort((a, b) => Number(b.time || 0) - Number(a.time || 0));
    return entries[0] || null;
  };

  const privateProperties = (entry) => {
    if (!entry?.token || !['compatibility', 'atelier', 'recommendation', 'garage'].includes(entry.source)) return {};
    const models = Array.isArray(entry.exact_models) ? entry.exact_models : [];
    const values = {
      '_AW Moat Schema': SCHEMA,
      '_AW Moat Source': entry.source,
      '_AW Moat Surface': entry.surface || 'pdp_compatibility',
      '_AW Moat Token': entry.token,
      '_AW Moat Platform': entry.platform || '',
      '_AW Moat Result': entry.result || '',
      '_AW Moat Score': String(Math.max(0, Math.min(100, Number(entry.score || 0)))),
      '_AW Moat Attribution Score': String(Math.max(0, Math.min(100, Number(entry.attribution_score || (entry.source === 'compatibility' ? 95 : 0))))),
      '_AW Moat Compatibility Score': entry.source === 'compatibility' ? String(Math.max(0, Math.min(100, Number(entry.score || 0)))) : '',
      '_AW Moat Model State': entry.model_state || 'none',
      '_AW Moat Exact Model': models.length === 1 ? models[0] : '',
      '_AW Moat Exact Models': models.length > 1 ? models.join(',') : '',
      '_AW Moat Captured At': entry.captured_at || ''
    };
    return Object.fromEntries(
      Object.entries(values)
        .filter(([, value]) => value !== '')
        .map(([key, value]) => [key, safe(value, 240)])
    );
  };

  const decorateBody = (body, entry) => {
    const props = privateProperties(entry);
    if (!Object.keys(props).length) return { body, changed: false };

    if (body instanceof FormData) {
      Object.entries(props).forEach(([key, value]) => body.set('properties[' + key + ']', value));
      return { body, changed: true };
    }
    if (body instanceof URLSearchParams) {
      Object.entries(props).forEach(([key, value]) => body.set('properties[' + key + ']', value));
      return { body, changed: true };
    }
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (Array.isArray(parsed?.items)) {
          parsed.items = parsed.items.map((item) => ({
            ...item,
            properties: { ...(item.properties || {}), ...props }
          }));
        } else if (parsed && typeof parsed === 'object') {
          parsed.properties = { ...(parsed.properties || {}), ...props };
        } else {
          return { body, changed: false };
        }
        return { body: JSON.stringify(parsed), changed: true };
      } catch (_) {
        try {
          const params = new URLSearchParams(body);
          Object.entries(props).forEach(([key, value]) => params.set('properties[' + key + ']', value));
          return { body: params.toString(), changed: true };
        } catch (_) {}
      }
    }
    return { body, changed: false };
  };

  document.addEventListener('change', (event) => {
    const root = event.target?.closest?.('[data-aw-compatibility]');
    if (root && event.target.matches('[data-aw-platform-select],[data-aw-manufacturer-select],[data-aw-replica-type-select]')) {
      rememberCompatibility(root);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const root = event.target?.closest?.('[data-aw-compatibility]');
    if (root && event.target.closest('[data-aw-platform-button],button,a')) {
      window.setTimeout(() => rememberCompatibility(root), 0);
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !String(form.action || '').includes('/cart/add')) return;
    const entry = currentAttribution();
    if (!entry) return;
    const props = privateProperties(entry);
    Object.entries(props).forEach(([key, value]) => {
      const name = 'properties[' + key + ']';
      let input = [...form.elements].find((el) => el?.name === name);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    });
    track('moat_add_to_cart_attributed', {
      source: entry.source,
      surface: entry.surface,
      model_state: entry.model_state,
      exact_model_count: entry.exact_models?.length || 0,
      compatibility_score: entry.source === 'compatibility' ? (entry.score || 0) : 0,
      attribution_score: entry.attribution_score || (entry.source === 'compatibility' ? 95 : 0)
    });
  }, true);

  if (previousFetch) {
    window.fetch = (input, init = {}) => {
      let nextInit = init;
      let attributed = false;
      try {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const method = String(init?.method || input?.method || 'GET').toUpperCase();
        if (method === 'POST' && /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(url) && Object.prototype.hasOwnProperty.call(init, 'body')) {
          const entry = currentAttribution();
          if (entry) {
            const patched = decorateBody(init.body, entry);
            if (patched.changed) {
              nextInit = { ...init, body: patched.body };
              attributed = true;
            }
          }
        }
      } catch (_) {}
      const response = previousFetch(input, nextInit);
      if (!attributed) return response;
      return response.then((res) => {
        if (res?.ok) {
          const entry = currentAttribution();
          track('moat_attributed_cart_confirmed', {
            source: entry?.source || '',
            surface: entry?.surface || '',
            model_state: entry?.model_state || 'none',
            exact_model_count: entry?.exact_models?.length || 0,
            compatibility_score: entry?.source === 'compatibility' ? (entry?.score || 0) : 0,
            attribution_score: entry?.attribution_score || (entry?.source === 'compatibility' ? 95 : 0)
          });
        }
        return res;
      });
    };
  }

  const appendMoatEvent = (type, payload = {}) => {
    const occurredAt = new Date().toISOString();
    let id = '';
    try { id = crypto.randomUUID(); }
    catch (_) { id = 'moat-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 11); }
    const event = {
      id,
      type: safe(type, 60),
      source: 'client_usage',
      authority: 'customer_state',
      proof: false,
      surface: 'mon_atelier',
      occurredAt,
      updatedAt: occurredAt,
      schemaVersion: 1,
      ...Object.fromEntries(
        Object.entries(payload)
          .filter(([, value]) => ['string','number','boolean'].includes(typeof value))
          .map(([key, value]) => [safe(key, 60), typeof value === 'string' ? safe(value, 180) : value])
      )
    };
    const cutoff = Date.now() - 365 * 24 * 60 * 60 * 1000;
    const previous = read(localStorage, MOAT_EVENTS_KEY, []);
    const events = (Array.isArray(previous) ? previous : [])
      .filter((item) => item?.id && (Date.parse(item.occurredAt || item.updatedAt || '') || Date.now()) >= cutoff);
    events.push(event);
    const bounded = events.slice(-500);
    write(localStorage, MOAT_EVENTS_KEY, bounded);
    window.dispatchEvent(new CustomEvent('aw:moat-event', { detail: { event } }));
    try {
      window.AWP22ServerSync?.queue?.('p210_moat_event');
      window.setTimeout(() => window.AWP22ServerSync?.syncNow?.('p210_moat_event'), 300);
    } catch (_) {}
    return event;
  };

  /* P2.10 persisted Atelier conversion exposures */
  window.addEventListener('aw:analytics', (event) => {
    const detail = event.detail || {};
    const name = String(detail.event || '');
    const payload = detail.payload || {};
    if (detail.suppressed === true) return;

    const isProductClick = name === 'assassin_workshop:atelier_product_clicked';
    const isPreparation = name === 'assassin_workshop:atelier_preparation_selected';
    if (!isProductClick && !isPreparation) return;

    const assistId = safe(payload.assist_id || '', 80);
    if (!assistId) return;

    const sourceDetail = safe(payload.source_detail || (isPreparation ? 'new_replica_preparation' : 'atelier'), 80);
    const targetHandle = safe(payload.target_product_handle || payload.product_handle || '', 120);
    appendMoatEvent('atelier_conversion_exposure', {
      id: 'atelier-exposure-' + assistId,
      token: assistId,
      source_detail: sourceDetail,
      target_product_handle: targetHandle,
      exposure_kind: isPreparation ? 'preparation_selected' : 'product_clicked'
    });
  });

  const initAtelier = () => {
    if (!/\/pages\/mon-atelier-airsoft(?:[/?#]|$)/i.test(location.pathname + location.search + location.hash)) return;
    const now = Date.now();
    const previous = read(localStorage, ATELIER_KEY, null);
    const isReturn = Boolean(previous?.last_seen_at && now - Number(previous.last_seen_at) >= ATELIER_RETURN_GAP);
    const visits = Math.max(0, Number(previous?.visits || 0)) + 1;
    const gapHours = previous?.last_seen_at ? Math.round((now - Number(previous.last_seen_at)) / 3600000) : 0;
    write(localStorage, ATELIER_KEY, { last_seen_at: now, visits });
    appendMoatEvent('atelier_open', { visits, return_visit: isReturn });
    track('moat_atelier_open', { visits, return_visit: isReturn });
    if (isReturn) {
      appendMoatEvent('atelier_return', { visits, gap_hours: gapHours });
      track('moat_atelier_return', { visits, gap_hours: gapHours });
    }
  };

  window.AWMoatAttribution = {
    version: VERSION,
    schema: SCHEMA,
    currentCompatibility,
    currentAttribution,
    captureCompatibility: () => rememberCompatibility(document.querySelector('[data-aw-compatibility]'))
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAtelier, { once: true });
  else initAtelier();
})();
